import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import EmailTemplateService, { TEMPLATE_CATEGORIES } from '../services/EmailTemplateService';
import EmailTemplateEditor from './EmailTemplateEditor';

const EmailTemplateManager = ({ isOpen, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Load templates on component mount
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  // Load templates from Firestore
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const userTemplates = await EmailTemplateService.getUserTemplates();
      setTemplates(userTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Filter templates based on search and category
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchTerm || 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Handle template creation
  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowEditor(true);
  };

  // Handle template editing
  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowEditor(true);
  };

  // Handle template deletion
  const handleDeleteTemplate = async (templateId) => {
    try {
      await EmailTemplateService.deleteTemplate(templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted successfully');
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Handle template duplication
  const handleDuplicateTemplate = async (template) => {
    try {
      const duplicatedTemplate = await EmailTemplateService.duplicateTemplate(template.id);
      setTemplates(prev => [duplicatedTemplate, ...prev]);
      toast.success('Template duplicated successfully');
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  // Handle template save from editor
  const handleTemplateSave = async (savedTemplate) => {
    if (selectedTemplate) {
      // Update existing template
      setTemplates(prev => prev.map(t => t.id === savedTemplate.id ? savedTemplate : t));
    } else {
      // Add new template
      setTemplates(prev => [savedTemplate, ...prev]);
    }
    setShowEditor(false);
    setSelectedTemplate(null);
  };

  // Handle editor cancel
  const handleEditorCancel = () => {
    setShowEditor(false);
    setSelectedTemplate(null);
  };

  // Get category display info
  const getCategoryInfo = (categoryId) => {
    return TEMPLATE_CATEGORIES.find(cat => cat.id === categoryId) || 
           { id: categoryId, name: categoryId, icon: '' };
  };

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Strip HTML tags for preview display
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // Remove HTML tags and decode entities
    const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    return text.trim();
  };

  // Generate clean preview text
  const generatePreviewText = (template) => {
    const cleanContent = stripHtmlTags(template.content);
    return cleanContent.length > 120 ? cleanContent.substring(0, 120) + '...' : cleanContent;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-[#111111] rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-[#1A1A1A] shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Email Templates
            </h2>
            <p className="text-gray-600 dark:text-[#666666] mt-1">
              Create and manage your custom email templates
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#333333] flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-[#666666]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search and Create */}
        <div className="p-6 border-b border-gray-200 dark:border-[#1A1A1A] shrink-0">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-[#666666]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-[#666666] transition-all duration-200"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white transition-all duration-200"
            >
              <option value="all">All Categories</option>
              {TEMPLATE_CATEGORIES.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            {/* Create Button */}
            <button
              onClick={handleCreateTemplate}
              className="px-6 py-3 bg-brand text-white rounded-xl hover:bg-[#E6A15E] transition-colors flex items-center gap-2 font-medium whitespace-nowrap"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Template
            </button>
          </div>
        </div>

        {/* Templates List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand"></div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-[#1A1A1A] rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400 dark:text-[#666666]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {searchTerm || selectedCategory !== 'all' ? 'No templates found' : 'No templates yet'}
              </h3>
              <p className="text-gray-600 dark:text-[#666666] mb-6">
                {searchTerm || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first email template to get started'}
              </p>
              {!searchTerm && selectedCategory === 'all' && (
                <button
                  onClick={handleCreateTemplate}
                  className="px-6 py-3 bg-brand text-white rounded-xl hover:bg-[#E6A15E] transition-colors font-medium"
                >
                  Create Your First Template
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => {
                const categoryInfo = getCategoryInfo(template.category);
                
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-[#0A0A0A] rounded-xl border border-gray-200 dark:border-[#1A1A1A] p-6 hover:shadow-lg hover:border-gray-300 dark:hover:border-[#333333] transition-all duration-200 group"
                  >
                    {/* Template Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-brand bg-brand/10 px-2 py-1 rounded-full">
                            {categoryInfo.name}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1">
                          {template.name}
                        </h3>
                        {template.description && (
                          <p className="text-sm text-gray-600 dark:text-[#666666] line-clamp-2">
                            {template.description}
                          </p>
                        )}
                      </div>
                      
                      {/* Actions Menu */}
                      <div className="relative">
                        <button 
                          className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#333333] flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            const menu = e.currentTarget.nextElementSibling;
                            menu.classList.toggle('hidden');
                          }}
                        >
                          <svg className="w-4 h-4 text-gray-600 dark:text-[#666666]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        
                        <div className="absolute right-0 top-full mt-2 w-40 bg-white dark:bg-[#1A1A1A] border border-gray-200 dark:border-[#333333] rounded-xl shadow-lg z-10 hidden">
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="w-full px-4 py-3 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors rounded-t-xl"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDuplicateTemplate(template)}
                            className="w-full px-4 py-3 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors"
                          >
                            Duplicate
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(template.id)}
                            className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-b-xl"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Template Preview */}
                    <div className="bg-gray-50 dark:bg-[#1A1A1A] rounded-lg p-4 mb-4 border border-gray-200 dark:border-[#333333]">
                      <div className="text-xs text-gray-600 dark:text-[#666666] mb-2">
                        Preview:
                      </div>
                      <div className="text-sm text-gray-900 dark:text-white line-clamp-3 whitespace-pre-wrap">
                        {generatePreviewText(template)}
                      </div>
                    </div>

                    {/* Template Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-[#666666]">
                      <div className="flex items-center gap-4">
                        <span>{template.variables?.length || 0} variables</span>
                        <span>{template.usageCount || 0} uses</span>
                      </div>
                      <span>
                        {formatDate(template.updatedAt)}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-10"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-[#111111] rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Delete Template
                </h3>
                <p className="text-gray-600 dark:text-[#666666] mb-6">
                  Are you sure you want to delete this template? This action cannot be undone.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 px-4 py-2 bg-gray-100 dark:bg-[#1A1A1A] text-gray-900 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(deleteConfirm)}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Template Editor Modal */}
      <EmailTemplateEditor
        isOpen={showEditor}
        template={selectedTemplate}
        onSave={handleTemplateSave}
        onCancel={handleEditorCancel}
      />
    </motion.div>
  );
};

export default EmailTemplateManager; 